import {gql} from '@apollo/client'
import React, {ReactNode, useEffect, useState} from 'react'
import {Helmet} from 'react-helmet-async'
import {LinkCrumbtrail} from '../../components/crumbtrail'
import {
  PageBody,
  PageDescription,
  PageHeader,
  PageHeaderContent
} from '../../components/page-styles'
import styled from 'styled-components'
import {Loader} from '../../components/loader'
import {
  PermissionAction,
  PermissionResource
} from '../../data/workbench-api/generated/types'
import {EnvironmentNode} from '../../environment'
import {useAnalytics} from '../../lib/analytics/analytics'
import {ResultContainer} from './results'
import {AFBulkCSVInput} from './csv-input'
import {useCustomerFields} from '../customers/hooks/use-fields'
import {useListCustomEventsQuery} from './generated'
import {Checkbox} from '../../components/library/form/checkbox'
import {useFlags} from '../../hooks/use-flags'
import {Help} from './help'
import {IconButton} from '../../components/library/buttons'
import {NoResultsPermissions} from '../../components/library/list'

import {Dropdown} from '../../components/library/dropdown/dropdown'
import {
  DropdownBody,
  DropdownItem
} from '../../components/library/dropdown/content'
import {Icon} from '../../components/library/icon/icon'
import SmallSearchIcon from '../../assets/icons/search-small.svg'
import {useFocusFirstFormInput} from '../../lib/hooks'
import {useActionFlowConfig} from '../../hooks/use-action-flow-config'

const SearchBox = styled.form`
  border: 1px solid ${({theme}) => theme.grey10};
  border-radius: 4px;
  padding: 7px 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 6px 16px 16px 12px;
`

const StyledSearchIcon = styled(SmallSearchIcon)`
  color: ${({theme}) => theme.black};
`

const SearchInput = styled.input`
  border: none;
  margin: 0;
  padding: 0;
  overflow: hidden;
  flex-grow: 1;
  font-size: ${({theme}) => theme.fontSizeXS};
`

const ItemId = styled.div`
  color: ${({theme}) => theme.colors.grey10};
`

const OptionItem = styled.a`
  font-size: ${({theme}) => theme.fontSizeXS};
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 30px !important;
  height: 30px;
`

const DropdownWrapper = styled.a<{padding: string}>`
  padding: ${p => p.padding};
  border-radius: 4px;
  background-color: ${p => p.theme.colors.grey3};
  display: block;
`

const LightText = styled.span`
  color: ${p => p.theme.colors.grey10};
`

const LightTextSmall = styled.span`
  color: ${p => p.theme.colors.grey10};
  font-size: ${p => p.theme.fontSizeS};
`

export const AfBulkSend = ({environment}: {environment: EnvironmentNode}) => {
  const analytics = useAnalytics()
  const canUse = environment.hasPermission(
    PermissionResource.CardInstance,
    PermissionAction.Edit
  )

  const flags = useFlags(environment.organisationId)

  const {data: customEventsData, loading: eventsLoading} =
    useListCustomEventsQuery({
      variables: {
        organisationId: environment.organisationId,
        environmentId: environment.id
      }
    })
  const {fieldDefinitions} = useCustomerFields(
    customEventsData?.environment?.preferences,
    customEventsData?.customEventConfigList?.nodes ?? []
  )
  const possibleUserFields = fieldDefinitions.map(f => f.name)

  const [flowSearch, setFlowSearch] = useState('')
  const [selectedActionFlow, setSelectedActionFlow] = useState<{
    id: string
    name: string
  }>()
  const [isTestMode, setIsTestMode] = useState<boolean>(false)
  const {data: actionFlows, loading: actionFlowsLoading} = useActionFlowConfig({
    environmentId: environment.id
  })

  useEffect(() => {
    analytics.track({
      event: 'Viewed Action Flow bulk send tool',
      properties: {
        org_id: environment.organisationId,
        environment_id: environment.id,
        category: 'tools'
      }
    })
  }, [analytics, environment])

  const [showHelp, setShowHelp] = useState(false)

  if (eventsLoading || actionFlowsLoading) {
    return <Loader />
  }

  const flowSearchRegex = flowSearch
    ? new RegExp(`(${flowSearch})`, 'i')
    : undefined
  const filteredFlows = flowSearchRegex
    ? actionFlows?.filter(flow => flowSearchRegex.test(flow.name ?? 'Unnamed'))
    : actionFlows

  const title = flags.actionFlows ? 'Action Flow' : 'Card'

  return (
    <>
      <Helmet>
        <title>{title} bulk send</title>
      </Helmet>
      <PageHeader>
        <LinkCrumbtrail crumbs={[['../tools', 'Tools'], 'Operations']} />
        <PageHeaderContent>
          <h2>{title} bulk send</h2>
          <IconButton
            style={{margin: '2px'}}
            icon="info"
            size="large"
            onClick={() => setShowHelp(!showHelp)}
          />
        </PageHeaderContent>
      </PageHeader>
      <PageDescription>
        Upload a CSV file to send {title}s in bulk.
      </PageDescription>
      <PageBody>
        {canUse ? (
          <Wrapper>
            <FlexWrapper style={{maxWidth: '70%'}}>
              <div style={{marginBottom: '20px'}}>
                <Help environment={environment} showHelp={showHelp} />
                <Checkbox
                  label={`Run in test mode`}
                  checked={isTestMode}
                  onChange={setIsTestMode}
                />
                <Dropdown
                  trigger={(handleClick, ref) => (
                    <DropdownWrapper
                      padding={
                        selectedActionFlow
                          ? '10px 0 10px 20px;'
                          : '20px 0 20px 20px;'
                      }
                      onClick={handleClick}
                      ref={ref as React.Ref<HTMLAnchorElement>}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <div>
                          {selectedActionFlow ? (
                            <>
                              <LightTextSmall>{title}</LightTextSmall>
                              <br />
                              {selectedActionFlow.name}
                            </>
                          ) : (
                            <LightText>{`${`Select a${
                              title === 'Action Flow' ? 'n' : ''
                            } ${title}`}...`}</LightText>
                          )}
                        </div>
                        <div
                          style={{
                            float: 'right',
                            marginRight: '25px'
                          }}
                        >
                          <Icon name="chevron" rotate={90} />
                        </div>
                      </div>
                    </DropdownWrapper>
                  )}
                >
                  {closeDropdown => (
                    <>
                      <DropdownBody>
                        <Search value={flowSearch} onChange={setFlowSearch} />
                        {filteredFlows?.map(f => {
                          // any falsey value, fallback to 'Unnamed'
                          const flowName = f.name || 'Unnamed'
                          let display: ReactNode = flowName
                          if (flowSearchRegex) {
                            // Split the name into parts, and wrap the parts that match the search text <strong>
                            const parts = flowName.split(flowSearchRegex)
                            display = parts.map((part, index) =>
                              flowSearchRegex.test(part) ? (
                                <strong key={index}>{part}</strong>
                              ) : (
                                part
                              )
                            )
                          }
                          return (
                            <DropdownItem
                              key={`flow-config-${f.id}`}
                              // Ensure white space contnues to show when searching
                              style={{whiteSpace: 'pre-wrap'}}
                            >
                              <OptionItem
                                onClick={() => {
                                  setSelectedActionFlow({
                                    id: f.id,
                                    name: flowName
                                  })
                                  closeDropdown()
                                }}
                              >
                                <div>{display}</div>
                                <ItemId>ID: {f.id}</ItemId>
                              </OptionItem>
                            </DropdownItem>
                          )
                        })}
                      </DropdownBody>
                    </>
                  )}
                </Dropdown>
              </div>
              <AFBulkCSVInput
                environment={environment}
                possibleUserFields={possibleUserFields}
                selectedActionFlowId={selectedActionFlow?.id}
                isTestMode={isTestMode}
              />
            </FlexWrapper>
            <FlexWrapper>
              <ResultContainer
                environment={environment}
                actionFlows={actionFlows}
                title={title}
              />
            </FlexWrapper>
          </Wrapper>
        ) : (
          <NoResultsPermissions message="Sorry, you don't have permission to use this tool" />
        )}
      </PageBody>
    </>
  )
}

const Search = ({
  onChange,
  value
}: {
  onChange: (text: string) => void
  value: string
}) => {
  const ref = useFocusFirstFormInput()
  return (
    <SearchBox onSubmit={e => e.preventDefault()} ref={ref}>
      <StyledSearchIcon />
      <SearchInput
        onChange={e => onChange(e.target.value)}
        placeholder="Search..."
        type="search"
        value={value}
      />
    </SearchBox>
  )
}

const FlexWrapper = styled.div`
  flex: 1;
`

const Wrapper = styled.div<{disabled?: boolean}>`
  display: flex;
  gap: 30px;

  ${props =>
    props.disabled &&
    `
      opacity: 0.3;
      pointer-events: none;
    `}
`

gql`
  query ListCustomEvents($organisationId: String!, $environmentId: String!) {
    customEventConfigList(
      organisationId: $organisationId
      environmentId: $environmentId
    ) {
      nodes {
        ...UseCustomerFieldsViewCustomEventConfig
      }
      totalCount
    }
    environment(id: $environmentId) {
      preferences
    }
  }

  ${useCustomerFields.fragments.customEventConfig}
`

AfBulkSend.fragments = {
  actionFlows: gql`
    fragment AfBulkSendActionFlow on ActionFlowConfig {
      id
      name
      isDefault
    }
  `
}
